import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getToken, setToken, clearToken } from './App';
import i18n from './i18n';
import Layout from './Layout';
import { Ico } from './icons';


const formatDate = (iso, locale) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString(locale, {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    } catch {
        return iso;
    }
};

const maskApiKey = (key) => {
    if (!key || key.length < 12) return key;
    return `${key.slice(0, 6)}…${key.slice(-4)}`;
};

const initials = (name) => {
    const p = (name || '?').trim().split(/\s+/).filter(Boolean);
    if (p.length === 0) return '?';
    if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
    return (p[0][0] + p[p.length - 1][0]).toUpperCase();
};

function Toast({ toast }) {
    if (!toast) return null;
    const cls = toast.type === 'error' ? 'toast toast-error'
        : toast.type === 'success' ? 'toast toast-success'
        : 'toast toast-info';
    return (
        <div className={cls} role="alert">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
                <Ico name={toast.type === 'error' ? 'alert' : toast.type === 'success' ? 'check' : 'info'} size={16} />
                <span>
                    {toast.type === 'error' ? 'Error' : toast.type === 'success' ? 'OK' : 'Info'}
                </span>
            </div>
            <div>{toast.message}</div>
            {toast.detail && (
                <div className="toast-detail">
                    <code>{toast.detail}</code>
                </div>
            )}
            {toast.onClose && (
                <button className="toast-close" onClick={toast.onClose} aria-label="×">×</button>
            )}
        </div>
    );
}

function TenantModal({ initial, mode, onSave, onClose }) {
    const { t } = useTranslation();
    const [name, setName] = useState(initial?.name || '');
    const [active, setActive] = useState(initial?.active !== false);
    const [adminEmail, setAdminEmail] = useState(initial?.admin_email || '');
    const [adminName, setAdminName] = useState(initial?.admin_name || '');
    const [adminPassword, setAdminPassword] = useState('');
    const [showPw, setShowPw] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        setError('');
        const trimmedName = name.trim();
        if (!trimmedName || trimmedName.length < 2) {
            setError(t('superadmin.err_name_min'));
            return;
        }
        const payload = { name: trimmedName };
        if (mode === 'edit') payload.active = active;

        const trimmedEmail = adminEmail.trim();
        const trimmedAdminName = adminName.trim();
        if (mode === 'create') {
            if (!trimmedEmail) {
                setError(t('superadmin.err_admin_email_required'));
                return;
            }
            if (!trimmedAdminName) {
                setError(t('superadmin.err_admin_name_required'));
                return;
            }
            if (!adminPassword) {
                setError(t('superadmin.err_admin_password_required'));
                return;
            }
            payload.admin_email = trimmedEmail;
            payload.admin_name = trimmedAdminName;
            payload.admin_password = adminPassword;
        } else {
            if (trimmedEmail) payload.admin_email = trimmedEmail;
            if (trimmedAdminName) payload.admin_name = trimmedAdminName;
            if (adminPassword !== '') payload.admin_password = adminPassword;
        }
        onSave(payload);
    };

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                <div className="modal-head">
                    <div className="modal-ico"><Ico name="building" size={18} /></div>
                    <div className="modal-title">
                        {mode === 'create' ? t('superadmin.modal_create_title') : t('superadmin.modal_edit_title')}
                    </div>
                    <button type="button" className="btn-icon modal-x" onClick={onClose} aria-label={t('common.cancel')}>
                        <Ico name="close" size={16} />
                    </button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="field">
                        <label className="field-label" htmlFor="tnt-name">
                            {t('superadmin.field_tenant_name')}
                        </label>
                        <div className="control">
                            <span className="control-ico"><Ico name="building" size={16} /></span>
                            <input
                                id="tnt-name"
                                className="input pad-ico"
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder={t('superadmin.field_tenant_name_ph')}
                                maxLength={100}
                                required
                                autoFocus
                            />
                        </div>
                    </div>

                    {mode === 'edit' && (
                        <label className="switch" style={{ marginBottom: 12 }}>
                            <input
                                type="checkbox"
                                checked={active}
                                onChange={(e) => setActive(e.target.checked)}
                            />
                            <span className="switch-track"><span className="switch-dot" /></span>
                            <span className="switch-label">{t('superadmin.field_active')}</span>
                        </label>
                    )}

                    <fieldset className="fieldset">
                        <legend>
                            {mode === 'create'
                                ? t('superadmin.fieldset_create_legend')
                                : t('superadmin.fieldset_edit_legend')}
                        </legend>
                        <p className="hint">
                            {mode === 'create'
                                ? t('superadmin.fieldset_create_hint')
                                : t('superadmin.fieldset_edit_hint')}
                        </p>

                        <div className="field">
                            <label className="field-label" htmlFor="tnt-admin-email">
                                {t('superadmin.field_admin_email')}{mode === 'create' ? ' *' : ''}
                            </label>
                            <div className="control">
                                <span className="control-ico"><Ico name="mail" size={16} /></span>
                                <input
                                    id="tnt-admin-email"
                                    className="input pad-ico"
                                    type="email"
                                    value={adminEmail}
                                    onChange={(e) => setAdminEmail(e.target.value)}
                                    placeholder={t('superadmin.field_admin_email_ph')}
                                    maxLength={150}
                                    autoComplete="off"
                                    required={mode === 'create'}
                                />
                            </div>
                        </div>

                        <div className="field">
                            <label className="field-label" htmlFor="tnt-admin-name">
                                {t('superadmin.field_admin_name')}{mode === 'create' ? ' *' : ''}
                            </label>
                            <div className="control">
                                <span className="control-ico"><Ico name="user" size={16} /></span>
                                <input
                                    id="tnt-admin-name"
                                    className="input pad-ico"
                                    type="text"
                                    value={adminName}
                                    onChange={(e) => setAdminName(e.target.value)}
                                    placeholder={t('superadmin.field_admin_name_ph')}
                                    maxLength={100}
                                    autoComplete="off"
                                    required={mode === 'create'}
                                />
                            </div>
                        </div>

                        <div className="field">
                            <label className="field-label" htmlFor="tnt-admin-pw">
                                {mode === 'create' ? t('superadmin.field_admin_password_create') : t('superadmin.field_admin_password_edit')}
                            </label>
                            <div className="pw-row">
                                <div className="control" style={{ flex: 1 }}>
                                    <span className="control-ico"><Ico name="lock" size={16} /></span>
                                    <input
                                        id="tnt-admin-pw"
                                        className="input pad-ico pad-end"
                                        type={showPw ? 'text' : 'password'}
                                        value={adminPassword}
                                        onChange={(e) => setAdminPassword(e.target.value)}
                                        placeholder={mode === 'create' ? t('superadmin.field_password_ph_create') : t('superadmin.field_password_ph_edit')}
                                        minLength={8}
                                        autoComplete="new-password"
                                        required={mode === 'create'}
                                    />
                                    <button
                                        type="button"
                                        className="input-affix"
                                        onClick={() => setShowPw(!showPw)}
                                        title={showPw ? t('common.hide') : t('common.show')}
                                        aria-label={showPw ? t('common.hide') : t('common.show')}
                                    >
                                        <Ico name={showPw ? 'eyeOff' : 'eye'} size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </fieldset>

                    {error && (
                        <div className="alert alert-error" role="alert">
                            <Ico name="alert" size={17} />
                            <span>{error}</span>
                        </div>
                    )}

                    <div className="modal-actions">
                        <button type="button" onClick={onClose} className="btn btn-ghost">
                            {t('common.cancel')}
                        </button>
                        <button type="submit" className="btn btn-primary">
                            <Ico name={mode === 'create' ? 'plus' : 'check'} size={15} />
                            {mode === 'create' ? t('superadmin.submit_create') : t('superadmin.submit_edit')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function DeleteConfirmModal({ tenant, onConfirm, onClose }) {
    const { t } = useTranslation();
    const [step, setStep] = useState(1);
    const [typed, setTyped] = useState('');

    const handleConfirm = () => {
        if (step === 1) {
            setStep(2);
        } else if (step === 2) {
            if (typed === t('superadmin.delete_modal_confirm_word')) {
                onConfirm();
            }
        }
    };

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal-card modal-danger" onClick={(e) => e.stopPropagation()}>
                <div className="modal-head">
                    <div className="modal-ico danger"><Ico name="trash" size={18} /></div>
                    <div className="modal-title">{t('superadmin.delete_modal_title')}</div>
                    <button type="button" className="btn-icon modal-x" onClick={onClose} aria-label={t('common.cancel')}>
                        <Ico name="close" size={16} />
                    </button>
                </div>
                {step === 1 && (
                    <div>
                        <p>
                            {t('superadmin.delete_modal_step1_intro')}{' '}
                            <strong>{tenant?.name}</strong>{' '}
                            (<code>{tenant?.slug}</code>).
                        </p>
                        <p className="warning">
                            {t('superadmin.delete_modal_step1_warning')}
                        </p>
                        <ul>
                            <li>{t('superadmin.delete_modal_li_tenant')}</li>
                            <li>{t('superadmin.delete_modal_li_users')}</li>
                            <li>{t('superadmin.delete_modal_li_employees')}</li>
                            <li>{t('superadmin.delete_modal_li_logs')}</li>
                        </ul>
                        <p className="info">
                            {t('superadmin.delete_modal_step1_backup')}{' '}
                            <code>/opt/nexus/backups/tenants/</code>.
                        </p>
                        <div className="modal-actions">
                            <button type="button" onClick={onClose} className="btn btn-ghost">
                                {t('common.cancel')}
                            </button>
                            <button type="button" onClick={handleConfirm} className="btn btn-danger">
                                {t('common.continue')}
                            </button>
                        </div>
                    </div>
                )}
                {step === 2 && (
                    <div>
                        <p>
                            {t('superadmin.delete_modal_step2_intro')} <code>{t('superadmin.delete_modal_step2_code')}</code> {t('superadmin.delete_modal_step2_after')}
                        </p>
                        <div className="field">
                            <div className="control">
                                <input
                                    type="text"
                                    value={typed}
                                    onChange={(e) => setTyped(e.target.value)}
                                    placeholder={t('superadmin.delete_modal_step2_ph')}
                                    autoFocus
                                    className="code-input"
                                />
                            </div>
                        </div>
                        <div className="modal-actions">
                            <button type="button" onClick={() => setStep(1)} className="btn btn-ghost">
                                {t('common.back')}
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirm}
                                className="btn btn-danger"
                                disabled={typed !== t('superadmin.delete_modal_confirm_word')}
                            >
                                <Ico name="trash" size={15} />
                                {t('superadmin.delete_modal_step2_btn')}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function ProfileModal({ initial, onSave, onClose }) {
    const { t } = useTranslation();
    const [email, setEmail] = useState(initial?.email || '');
    const [fullName, setFullName] = useState(initial?.full_name || initial?.name || '');
    const [password, setPassword] = useState('');
    const [showPw, setShowPw] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        setError('');

        const payload = {};
        const trimmedEmail = email.trim();
        const trimmedName = fullName.trim();

        if (trimmedEmail && trimmedEmail !== (initial?.email || '')) {
            payload.email = trimmedEmail;
        }
        if (trimmedName && trimmedName !== (initial?.full_name || initial?.name || '')) {
            payload.full_name = trimmedName;
        }
        if (password !== '') {
            payload.password = password;
        }

        if (Object.keys(payload).length === 0) {
            setError(t('superadmin.profile_err_no_changes'));
            return;
        }
        onSave(payload);
    };

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                <div className="modal-head">
                    <div className="modal-ico"><Ico name="user" size={18} /></div>
                    <div className="modal-title">{t('superadmin.profile_modal_title')}</div>
                    <button type="button" className="btn-icon modal-x" onClick={onClose} aria-label={t('common.cancel')}>
                        <Ico name="close" size={16} />
                    </button>
                </div>
                <p className="hint">{t('superadmin.profile_modal_hint')}</p>
                <form onSubmit={handleSubmit}>
                    <div className="field">
                        <label className="field-label" htmlFor="prof-email">
                            {t('superadmin.profile_field_email')}
                        </label>
                        <div className="control">
                            <span className="control-ico"><Ico name="mail" size={16} /></span>
                            <input
                                id="prof-email"
                                className="input pad-ico"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder={t('superadmin.profile_field_email_ph')}
                                maxLength={150}
                                autoComplete="off"
                                autoFocus
                            />
                        </div>
                    </div>
                    <div className="field">
                        <label className="field-label" htmlFor="prof-name">
                            {t('superadmin.profile_field_full_name')}
                        </label>
                        <div className="control">
                            <span className="control-ico"><Ico name="user" size={16} /></span>
                            <input
                                id="prof-name"
                                className="input pad-ico"
                                type="text"
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                                placeholder={t('superadmin.profile_field_full_name_ph')}
                                maxLength={100}
                                autoComplete="off"
                            />
                        </div>
                    </div>
                    <div className="field">
                        <label className="field-label" htmlFor="prof-pw">
                            {t('superadmin.profile_field_password')}
                        </label>
                        <div className="control">
                            <span className="control-ico"><Ico name="lock" size={16} /></span>
                            <input
                                id="prof-pw"
                                className="input pad-ico pad-end"
                                type={showPw ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder={t('superadmin.profile_field_password_ph')}
                                minLength={8}
                                autoComplete="new-password"
                            />
                            <button
                                type="button"
                                className="input-affix"
                                onClick={() => setShowPw(!showPw)}
                                title={showPw ? t('common.hide') : t('common.show')}
                                aria-label={showPw ? t('common.hide') : t('common.show')}
                            >
                                <Ico name={showPw ? 'eyeOff' : 'eye'} size={16} />
                            </button>
                        </div>
                    </div>

                    {error && (
                        <div className="alert alert-error" role="alert">
                            <Ico name="alert" size={17} />
                            <span>{error}</span>
                        </div>
                    )}
                    <div className="modal-actions">
                        <button type="button" onClick={onClose} className="btn btn-ghost">
                            {t('common.cancel')}
                        </button>
                        <button type="submit" className="btn btn-primary">
                            <Ico name="check" size={15} />
                            {t('superadmin.profile_submit')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function TenantsAdmin() {
    const { t } = useTranslation();
    const [user, setUser] = useState(null);
    const [tenants, setTenants] = useState([]);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [toast, setToast] = useState(null);
    const [showCreate, setShowCreate] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [showProfile, setShowProfile] = useState(false);
    const navigate = useNavigate();

    const localeMap = { es: 'es-ES', ca: 'ca-ES', en: 'en-US' };
    const locale = localeMap[i18n.language?.split('-')[0]] || 'es-ES';

    const showToast = useCallback((type, message, detail = null) => {
        setToast({ type, message, detail, onClose: () => setToast(null) });
        if (type !== 'error') {
            setTimeout(() => setToast(null), 5000);
        }
    }, []);

    const loadTenants = useCallback(async () => {
        setLoading(true);
        setError('');
        const token = getToken();
        try {
            const url = new URL('/api/admin/tenants', window.location.origin);
            url.searchParams.set('page', String(page));
            url.searchParams.set('per_page', '20');
            if (search) url.searchParams.set('search', search);
            const res = await fetch(url.toString(), {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (res.status === 401) {
                clearToken();
                localStorage.removeItem('nexus_user');
                navigate('/login', { replace: true });
                return;
            }
            if (res.status === 403) {
                setError(t('superadmin.forbidden'));
                return;
            }
            if (!res.ok) {
                throw new Error(t('common.http_error', { status: res.status }));
            }
            const data = await res.json();
            setTenants(data.tenants || []);
            setPages(data.pages || 1);
            setTotal(data.total || 0);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [page, search, navigate, t]);

    useEffect(() => {
        try {
            const u = JSON.parse(localStorage.getItem('nexus_user') || 'null');
            setUser(u);
            if (u?.role !== 'superadmin') {
                navigate('/dashboard', { replace: true });
                return;
            }
        } catch {
            navigate('/dashboard', { replace: true });
            return;
        }
        loadTenants();
    }, [loadTenants, navigate]);

    const handleProfileSave = async (payload) => {
        const token = getToken();
        try {
            const res = await fetch('/api/me', {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });
            const data = await res.json();
            if (!res.ok) {
                throw new Error(data.error || t('common.http_error', { status: res.status }));
            }
            if (data.token) {
                setToken(data.token);
            }
            if (data.user) {
                localStorage.setItem('nexus_user', JSON.stringify(data.user));
                setUser(data.user);
            }
            setShowProfile(false);
            const labels = {
                email: t('superadmin.profile_field_email_label'),
                full_name: t('superadmin.profile_field_name_label'),
                password: t('superadmin.profile_field_password_label'),
                preferred_language: t('superadmin.profile_field_language_label'),
            };
            const changed = (data.changed || []).map((c) => labels[c] || c).join(', ');
            showToast('success', t('superadmin.profile_toast_ok', { changed }));
        } catch (err) {
            showToast('error', t('superadmin.profile_toast_err', { message: err.message }));
        }
    };

    const handleCreate = async (payload) => {
        const token = getToken();
        try {
            const res = await fetch('/api/admin/tenants', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });
            const data = await res.json();
            if (!res.ok) {
                throw new Error(data.error || t('common.http_error', { status: res.status }));
            }
            setShowCreate(false);
            showToast('success', t('superadmin.toast_created', { name: data.name, slug: data.slug }));
            loadTenants();
        } catch (err) {
            showToast('error', t('superadmin.toast_create_err', { message: err.message }));
        }
    };

    const handleEdit = async (payload) => {
        if (!editTarget) return;
        const token = getToken();
        try {
            const res = await fetch(`/api/admin/tenants/${editTarget.id}`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });
            const data = await res.json();
            if (!res.ok) {
                throw new Error(data.error || t('common.http_error', { status: res.status }));
            }
            setEditTarget(null);
            const changed = [];
            if (payload.admin_email) changed.push(t('superadmin.admin_field_email'));
            if (payload.admin_name) changed.push(t('superadmin.admin_field_name'));
            if (payload.admin_password) changed.push(t('superadmin.admin_field_password'));
            if (changed.length) {
                showToast('success', t('superadmin.toast_updated_with_admin', { name: data.name, fields: changed.join(', ') }));
            } else {
                showToast('success', t('superadmin.toast_updated', { name: data.name }));
            }
            loadTenants();
        } catch (err) {
            showToast('error', t('superadmin.toast_update_err', { message: err.message }));
        }
    };

    const openEdit = async (tenant) => {
        const token = getToken();
        try {
            const res = await fetch(`/api/admin/tenants/${tenant.id}`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (res.status === 401) {
                clearToken();
                navigate('/login');
                return;
            }
            if (!res.ok) {
                throw new Error(t('common.http_error', { status: res.status }));
            }
            const data = await res.json();
            setEditTarget(data);
        } catch (err) {
            setEditTarget(tenant);
            showToast('info', t('superadmin.toast_admin_detail_err', { message: err.message }));
        }
    };

    const handleToggleActive = async (tenant) => {
        const token = getToken();
        const action = tenant.active ? 'deactivate' : 'activate';
        try {
            const res = await fetch(`/api/admin/tenants/${tenant.id}/${action}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
            });
            const data = await res.json();
            if (!res.ok) {
                throw new Error(data.error || t('common.http_error', { status: res.status }));
            }
            showToast('success', action === 'activate'
                ? t('superadmin.toast_activated', { name: data.name })
                : t('superadmin.toast_deactivated', { name: data.name }));
            loadTenants();
        } catch (err) {
            showToast('error', t('superadmin.toast_toggle_err', { message: err.message }));
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        const token = getToken();
        try {
            const res = await fetch(`/api/admin/tenants/${deleteTarget.id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ confirm: 'BORRAR' }),
            });
            const data = await res.json();
            if (!res.ok) {
                throw new Error(data.error || t('common.http_error', { status: res.status }));
            }
            setDeleteTarget(null);
            showToast(
                'success',
                t('superadmin.toast_deleted', { name: data.name }),
                t('superadmin.toast_deleted_detail', { file: data.backup_file }),
            );
            loadTenants();
        } catch (err) {
            showToast('error', t('superadmin.toast_delete_err', { message: err.message }));
        }
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setPage(1);
        loadTenants();
    };

    return (
        <Layout
            title={t('nav.tenants', 'Tenants')}
            subtitle={t('superadmin.subtitle')}
            actions={
                <button
                    onClick={() => setShowProfile(true)}
                    className="btn btn-ghost btn-sm"
                    title={t('superadmin.my_profile_title')}
                >
                    <Ico name="user" size={14} />
                    {t('superadmin.my_profile_plain', 'Mi perfil')}
                </button>
            }
        >
            <Toast toast={toast} />

            <section className="card">
                <div className="card-head">
                    <div>
                        <div className="card-title">
                            {t('superadmin.tenants_title', 'Tenants registrados')}
                        </div>
                        <div className="card-sub">
                            <span className="card-count">{total}</span>
                        </div>
                    </div>
                    <div className="card-tools">
                        <button
                            className="btn btn-primary"
                            onClick={() => setShowCreate(true)}
                        >
                            <Ico name="plus" size={16} />
                            {t('superadmin.new_tenant_plain', 'Nuevo tenant')}
                        </button>
                    </div>
                </div>

                <form className="filter-bar" onSubmit={handleSearchSubmit}>
                    <div className="search-wrap">
                        <Ico name="search" size={16} />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={t('superadmin.search_ph')}
                            className="search-input"
                        />
                    </div>
                    <button type="submit" className="btn btn-ghost">
                        <Ico name="search" size={14} />
                        {t('common.search')}
                    </button>
                    {search && (
                        <button
                            type="button"
                            className="btn-link"
                            onClick={() => { setSearch(''); setPage(1); }}
                        >
                            {t('common.clear')}
                        </button>
                    )}
                </form>

                {error && (
                    <div className="alert alert-error" role="alert">
                        <Ico name="alert" size={17} />
                        <span>{error}</span>
                    </div>
                )}

                {loading ? (
                    <div className="skel-rows">
                        {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="skel skel-row" />)}
                    </div>
                ) : (
                    <div className="table-wrap">
                        <table className="nx-table">
                            <thead>
                                <tr>
                                    <th>{t('superadmin.table_name')}</th>
                                    <th>{t('superadmin.table_slug')}</th>
                                    <th>{t('superadmin.table_api_key')}</th>
                                    <th>{t('superadmin.table_active')}</th>
                                    <th>{t('superadmin.table_created')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('superadmin.table_actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tenants.length === 0 ? (
                                    <tr>
                                        <td colSpan={6}>
                                            <div className="empty-state">
                                                <div className="empty-ico"><Ico name="building" size={27} /></div>
                                                <div className="empty-title">{t('superadmin.empty_title', 'Sin tenants')}</div>
                                                <div className="empty-sub">{t('superadmin.no_tenants')}</div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : tenants.map((tnt) => (
                                    <tr key={tnt.id} className={!tnt.active ? 'row-inactive' : ''}>
                                        <td>
                                            <div className="emp-cell">
                                                <div className="avatar sm">{initials(tnt.name)}</div>
                                                <div className="emp-meta">
                                                    <div className="emp-name">{tnt.name}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td><span className="chip-code">{tnt.slug}</span></td>
                                        <td>
                                            <span className="chip-code" title={tnt.api_key}>
                                                <Ico name="key" size={11} style={{ display: 'inline', verticalAlign: '-1px', marginRight: 4 }} />
                                                {maskApiKey(tnt.api_key)}
                                            </span>
                                        </td>
                                        <td>
                                            {tnt.active
                                                ? <span className="badge badge-success"><span className="dot pulse" />{t('common.yes')}</span>
                                                : <span className="badge badge-muted"><span className="dot" />{t('common.no')}</span>}
                                        </td>
                                        <td className="num">{formatDate(tnt.created_at, locale)}</td>
                                        <td className="actions-cell" style={{ textAlign: 'right' }}>
                                            <button
                                                className="btn-icon"
                                                title={t('superadmin.action_edit')}
                                                aria-label={t('superadmin.action_edit')}
                                                onClick={() => openEdit(tnt)}
                                            >
                                                <Ico name="pencil" size={15} />
                                            </button>
                                            <button
                                                className="btn-icon"
                                                title={tnt.active ? t('superadmin.action_deactivate') : t('superadmin.action_activate')}
                                                aria-label={tnt.active ? t('superadmin.action_deactivate') : t('superadmin.action_activate')}
                                                onClick={() => handleToggleActive(tnt)}
                                            >
                                                <Ico name={tnt.active ? 'pause' : 'play'} size={14} />
                                            </button>
                                            <button
                                                className="btn-icon danger"
                                                title={t('superadmin.action_delete')}
                                                aria-label={t('superadmin.action_delete')}
                                                onClick={() => setDeleteTarget(tnt)}
                                            >
                                                <Ico name="trash" size={15} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {pages > 1 && (
                    <div className="pagination">
                        <button
                            className="btn btn-ghost btn-sm"
                            disabled={page <= 1}
                            onClick={() => setPage(page - 1)}
                        >
                            <Ico name="chevronLeft" size={14} />
                            {t('common.previous_plain', 'Anterior')}
                        </button>
                        <span className="page-info">
                            {t('common.page_of', { page, pages })}
                        </span>
                        <button
                            className="btn btn-ghost btn-sm"
                            disabled={page >= pages}
                            onClick={() => setPage(page + 1)}
                        >
                            {t('common.next_plain', 'Siguiente')}
                            <Ico name="chevronRight" size={14} />
                        </button>
                    </div>
                )}
            </section>

            {showCreate && (
                <TenantModal
                    mode="create"
                    onSave={handleCreate}
                    onClose={() => setShowCreate(false)}
                />
            )}

            {editTarget && (
                <TenantModal
                    mode="edit"
                    initial={editTarget}
                    onSave={handleEdit}
                    onClose={() => setEditTarget(null)}
                />
            )}

            {deleteTarget && (
                <DeleteConfirmModal
                    tenant={deleteTarget}
                    onConfirm={handleDelete}
                    onClose={() => setDeleteTarget(null)}
                />
            )}

            {showProfile && (
                <ProfileModal
                    initial={user}
                    onSave={handleProfileSave}
                    onClose={() => setShowProfile(false)}
                />
            )}
        </Layout>
    );
}

export default TenantsAdmin;
